import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import {
  hqListMakes,
  hqGetModelsByMake,
  hqSubmitMake,
  hqSubmitModel,
} from '../../lib/hqApi';
import {
  IconArrowLeft,
  IconArrowRight,
  IconCheck,
  IconSearch,
  IconTractor,
  IconPlus,
  IconAlertCircle,
  IconBuildingFactory2,
  IconLawnMower,
  IconBackhoe,
  IconCarOffRoad,
} from '@tabler/icons-react';

const STEPS = ['Category', 'Make', 'Model', 'Details', 'Site', 'Operator', 'Confirm'];

const CATEGORIES = [
  { value: 'agricultural', label: 'Agricultural', Icon: IconTractor },
  { value: 'groundcare',   label: 'Groundcare',   Icon: IconLawnMower },
  { value: 'plant',        label: 'Plant',         Icon: IconBackhoe },
  { value: 'atv',          label: 'ATV',           Icon: IconCarOffRoad },
];

const normalise = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

export default function MachineOnboarding() {
  const navigate = useNavigate();
  const { orgId, userProfile } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Library data from HQ
  const [allMakes, setAllMakes] = useState([]);
  const [makesLoading, setMakesLoading] = useState(true);
  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [staff, setStaff] = useState([]);

  // Step 0 — Category
  const [selectedCategory, setSelectedCategory] = useState('');

  // Step 1 — Make
  const [makeSearch, setMakeSearch] = useState('');
  const [selectedMake, setSelectedMake] = useState(null);
  const [addingNewMake, setAddingNewMake] = useState(false);
  const [newMakeName, setNewMakeName] = useState('');
  const [newMakeCountry, setNewMakeCountry] = useState('');

  // Step 2 — Model
  const [modelSearch, setModelSearch] = useState('');
  const [selectedModel, setSelectedModel] = useState(null);
  const [addingNewModel, setAddingNewModel] = useState(false);
  const [newModelName, setNewModelName] = useState('');
  const [newModelYearRange, setNewModelYearRange] = useState('');

  // Step 3 — Details
  const [serialNumber, setSerialNumber] = useState('');
  const [registration, setRegistration] = useState('');
  const [year, setYear] = useState('');
  const [currentHours, setCurrentHours] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');

  // Step 4 — Site
  const [selectedSite, setSelectedSite] = useState(userProfile?.siteId || '');

  // Step 5 — Operator
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [isPoolMachine, setIsPoolMachine] = useState(false);

  // Load makes from HQ + sites/staff from Ops
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!orgId) return;
      try {
        const [hqResult, sitesSnap, staffSnap] = await Promise.all([
          hqListMakes(),
          getDocs(collection(db, 'organisations', orgId, 'sites')),
          getDocs(collection(db, 'organisations', orgId, 'staff')),
        ]);
        if (cancelled) return;
        setAllMakes(hqResult.makes || []);
        setSites(sitesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setStaff(staffSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Library load failed', err);
        if (!cancelled) setError('Could not load machine library: ' + err.message);
      } finally {
        if (!cancelled) setMakesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  // Load models when a canonical make is selected
  useEffect(() => {
    if (!selectedMake || selectedMake._pending) { setModels([]); return; }
    let cancelled = false;
    setModelsLoading(true);
    (async () => {
      try {
        const result = await hqGetModelsByMake(selectedMake.id, { category: selectedCategory });
        if (!cancelled) setModels(result.models || []);
      } catch (err) {
        if (!cancelled) setError('Could not load models: ' + err.message);
      } finally {
        if (!cancelled) setModelsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedMake, selectedCategory]);

  // Makes filtered by supported category + search
  const filteredMakes = useMemo(() => {
    return allMakes
      .filter(m => !selectedCategory || (m.supportedTypes || []).includes(selectedCategory))
      .filter(m => normalise(m.name).includes(normalise(makeSearch)));
  }, [allMakes, selectedCategory, makeSearch]);

  // Fuzzy match for new make submission
  const newMakeFuzzyMatch = useMemo(() => {
    if (!newMakeName || newMakeName.length < 2) return null;
    const n = normalise(newMakeName);
    return allMakes.find(m => {
      const en = normalise(m.name);
      return en.includes(n) || n.includes(en);
    }) || null;
  }, [newMakeName, allMakes]);

  // Fuzzy match for new model submission
  const filteredModels = useMemo(() => {
    return models.filter(m => normalise(m.name).includes(normalise(modelSearch))).sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { numeric: true }));
  }, [models, modelSearch]);

  const newModelFuzzyMatch = useMemo(() => {
    if (!newModelName || newModelName.length < 2) return null;
    const n = normalise(newModelName);
    return models.find(m => {
      const en = normalise(m.name);
      return en.includes(n) || n.includes(en);
    }) || null;
  }, [newModelName, models]);

  // ---------- Submit new make to HQ ----------
  const handleSubmitNewMake = async () => {
    if (!newMakeName.trim()) return;
    setSaving(true); setError('');
    try {
      const result = await hqSubmitMake({
        name: newMakeName.trim(),
        country: newMakeCountry.trim() || null,
        supportedTypes: [selectedCategory],
      });
      setSelectedMake({
        id: result.requestId,
        name: newMakeName.trim(),
        country: newMakeCountry.trim() || null,
        supportedTypes: [selectedCategory],
        _pending: true,
      });
      setAddingNewMake(false);
      setNewMakeName(''); setNewMakeCountry('');
      setStep(2);
    } catch (err) {
      setError('Submission failed: ' + err.message);
    }
    setSaving(false);
  };

  // ---------- Submit new model to HQ ----------
  const handleSubmitNewModel = async () => {
    if (!newModelName.trim() || !selectedMake) return;
    setSaving(true); setError('');
    try {
      const result = await hqSubmitModel({
        makeId: selectedMake.id,
        name: newModelName.trim(),
        yearRange: newModelYearRange.trim() || null,
        category: selectedCategory,
      });
      setSelectedModel({
        id: result.requestId,
        name: newModelName.trim(),
        yearRange: newModelYearRange.trim() || null,
        category: selectedCategory,
        _pending: true,
      });
      setAddingNewModel(false);
      setNewModelName(''); setNewModelYearRange('');
      setStep(3);
    } catch (err) {
      setError('Submission failed: ' + err.message);
    }
    setSaving(false);
  };

  // ---------- Final save to Ops Firestore ----------
  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const machineData = {
        // Display fields (cached for fast UI)
        make: selectedMake?.name || null,
        model: selectedModel?.name || null,
        makeLabel: selectedMake?.name || null,
        modelLabel: selectedModel?.name || null,
        category: selectedCategory,
        subCategory: selectedModel?.subCategory || null,
        heroImageUrl: selectedModel?.heroImageUrl || null,

        // Canonical references
        canonicalMakeId: selectedMake?.id || null,
        canonicalMakeStatus: selectedMake?._pending ? 'pending' : 'approved',
        canonicalModelId: selectedModel?.id || null,
        canonicalModelStatus: selectedModel?._pending ? 'pending' : 'approved',

        // Detail fields
        serialNumber: serialNumber.trim() || null,
        registration: registration.trim() || null,
        year: year ? parseInt(year) : null,
        currentHours: currentHours ? parseFloat(currentHours) : 0,
        purchaseDate: purchaseDate || null,
        purchasePrice: purchasePrice ? parseFloat(purchasePrice) : null,

        // Assignment
        siteId: selectedSite || null,
        operatorId: isPoolMachine ? null : selectedOperator?.id || null,
        isPoolMachine,

        // Status
        status: 'active',
        isBlocked: false,

        createdAt: serverTimestamp(),
        createdBy: userProfile?.uid || null,
      };

      await addDoc(collection(db, 'organisations', orgId, 'machines'), machineData);
      navigate('/machines');
    } catch (err) {
      setError('Save failed: ' + err.message);
    }
    setSaving(false);
  };

  // ---------- Step gating ----------
  const canAdvance = () => {
    if (step === 0) return !!selectedCategory;
    if (step === 1) return !!selectedMake;
    if (step === 2) return !!selectedModel;
    if (step === 3) return true;
    if (step === 4) return !!selectedSite;
    if (step === 5) return isPoolMachine || !!selectedOperator;
    return true;
  };

  const next = () => setStep(s => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep(s => Math.max(s - 1, 0));

  return (
    <Layout>
      <div style={{ padding: 28 }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 4 }}>Step {step + 1} of {STEPS.length}</div>
          <div style={{ fontSize: 22, fontWeight: 500 }}>{STEPS[step]}</div>
        </div>

        {/* Progress bar */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 28 }}>
          {STEPS.map((s, i) => (
            <div key={s} style={{
              flex: 1, height: 4, borderRadius: 2,
              background: i <= step ? 'var(--color-brand)' : 'var(--color-bg-tertiary)',
            }} />
          ))}
        </div>

        {error && (
          <div style={{ padding: '10px 14px', background: 'var(--color-danger-light)', color: 'var(--color-danger)', borderRadius: 'var(--radius-md)', fontSize: 12, marginBottom: 16 }}>
            {error}
          </div>
        )}

        {/* STEP 0 — Category */}
        {step === 0 && (
          <Card>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 16 }}>
              What type of machine are you adding?
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
              {CATEGORIES.map(cat => (
                <div key={cat.value} onClick={() => setSelectedCategory(cat.value)}
                  style={{
                    padding: 20, borderRadius: 'var(--radius-lg)', cursor: 'pointer',
                    border: selectedCategory === cat.value ? '2px solid var(--color-brand)' : '1px solid var(--color-border-light)',
                    background: selectedCategory === cat.value ? 'var(--color-brand-light)' : 'var(--color-bg-primary)',
                    textAlign: 'center', transition: 'all 0.15s',
                  }}>
                  <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center' }}><cat.Icon size={40} stroke={1.25} color={selectedCategory === cat.value ? 'var(--color-brand)' : 'var(--color-text-secondary)'} /></div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{cat.label}</div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* STEP 1 — Make */}
        {step === 1 && (
          <Card>
            {addingNewMake ? (
              <>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 16 }}>
                  Add a new manufacturer not in the library. It will be submitted for review and available immediately for your machines.
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Manufacturer name *</label>
                  <input value={newMakeName} onChange={e => setNewMakeName(e.target.value)} placeholder="e.g. Smith Engineering" autoFocus />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Country (optional)</label>
                  <input value={newMakeCountry} onChange={e => setNewMakeCountry(e.target.value)} placeholder="e.g. UK" />
                </div>
                {newMakeFuzzyMatch && (
                  <div style={{ padding: '10px 12px', background: 'var(--color-info-light)', borderRadius: 'var(--radius-md)', marginBottom: 16, fontSize: 12, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <IconAlertCircle size={16} stroke={1.5} color="var(--color-info)" />
                    <div style={{ flex: 1, color: 'var(--color-text-secondary)' }}>
                      Did you mean <strong>{newMakeFuzzyMatch.name}</strong>?
                      <div style={{ marginTop: 6 }}>
                        <Button size="sm" variant="secondary" onClick={() => { setSelectedMake(newMakeFuzzyMatch); setAddingNewMake(false); setNewMakeName(''); setNewMakeCountry(''); setStep(2); }}>
                          Use {newMakeFuzzyMatch.name}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="secondary" onClick={() => { setAddingNewMake(false); setNewMakeName(''); setNewMakeCountry(''); }}>Cancel</Button>
                  <Button loading={saving} onClick={handleSubmitNewMake} disabled={!newMakeName.trim()}>Submit and continue</Button>
                </div>
              </>
            ) : (
              <>
                <div style={{ position: 'relative', marginBottom: 16 }}>
                  <IconSearch size={14} stroke={1.5} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-tertiary)' }} />
                  <input value={makeSearch} onChange={e => setMakeSearch(e.target.value)} placeholder="Search manufacturer..." style={{ paddingLeft: 36 }} />
                </div>
                {makesLoading ? (
                  <div style={{ padding: 20, fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading library...</div>
                ) : (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 16, maxHeight: 380, overflow: 'auto' }}>
                      {filteredMakes.map(m => (
                        <div key={m.id} onClick={() => { setSelectedMake(m); setStep(2); }}
                          style={{
                            padding: 12, borderRadius: 'var(--radius-md)', cursor: 'pointer',
                            border: '1px solid var(--color-border-light)', background: 'var(--color-bg-primary)',
                            textAlign: 'center', transition: 'all 0.15s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--color-brand)'}
                          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--color-border-light)'}>
                          <div style={{ height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6 }}>
                            {m.logoUrl ? <img src={m.logoUrl} alt={m.name} style={{ maxWidth: '90%', maxHeight: 40, objectFit: 'contain' }} /> : <IconBuildingFactory2 size={22} stroke={1.2} color="var(--color-text-tertiary)" />}
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 500 }}>{m.name}</div>
                        </div>
                      ))}
                    </div>
                    {filteredMakes.length === 0 && !makesLoading && (
                      <div style={{ padding: 20, fontSize: 13, color: 'var(--color-text-tertiary)', textAlign: 'center' }}>
                        No matching {CATEGORIES.find(c => c.value === selectedCategory)?.label.toLowerCase()} makes.
                      </div>
                    )}
                    <Button variant="secondary" icon={<IconPlus size={14} stroke={1.5} />} onClick={() => setAddingNewMake(true)} fullWidth>
                      Can't find it? Add a new manufacturer
                    </Button>
                  </>
                )}
              </>
            )}
          </Card>
        )}

        {/* STEP 2 — Model */}
        {step === 2 && selectedMake && (
          <Card>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 16 }}>
              Selecting model for <strong>{selectedMake.name}</strong>
              {selectedMake._pending && <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 8px', borderRadius: 'var(--radius-full)', background: 'var(--color-warning-light)', color: 'var(--color-warning)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Pending review</span>}
            </div>

            {addingNewModel ? (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Model name *</label>
                  <input value={newModelName} onChange={e => setNewModelName(e.target.value)} placeholder="e.g. 6R 150" autoFocus />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Year range (optional)</label>
                  <input value={newModelYearRange} onChange={e => setNewModelYearRange(e.target.value)} placeholder="e.g. 2020–present" />
                </div>
                {newModelFuzzyMatch && (
                  <div style={{ padding: '10px 12px', background: 'var(--color-info-light)', borderRadius: 'var(--radius-md)', marginBottom: 16, fontSize: 12, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <IconAlertCircle size={16} stroke={1.5} color="var(--color-info)" />
                    <div style={{ flex: 1, color: 'var(--color-text-secondary)' }}>
                      Did you mean <strong>{newModelFuzzyMatch.name}</strong>?
                      <div style={{ marginTop: 6 }}>
                        <Button size="sm" variant="secondary" onClick={() => { setSelectedModel(newModelFuzzyMatch); setAddingNewModel(false); setNewModelName(''); setNewModelYearRange(''); setStep(3); }}>
                          Use {newModelFuzzyMatch.name}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="secondary" onClick={() => { setAddingNewModel(false); setNewModelName(''); setNewModelYearRange(''); }}>Cancel</Button>
                  <Button loading={saving} onClick={handleSubmitNewModel} disabled={!newModelName.trim()}>Submit and continue</Button>
                </div>
              </>
            ) : selectedMake._pending ? (
              <>
                <div style={{ padding: 14, background: 'var(--color-warning-light)', color: 'var(--color-warning)', borderRadius: 'var(--radius-md)', fontSize: 12, marginBottom: 16, lineHeight: 1.5 }}>
                  This make is pending HQ review, so we don't have its models yet. Add the model you need — it'll also be submitted for review.
                </div>
                <Button icon={<IconPlus size={14} stroke={1.5} />} onClick={() => setAddingNewModel(true)} fullWidth>
                  Add new model
                </Button>
              </>
            ) : (
              <>
                <div style={{ position: 'relative', marginBottom: 16 }}>
                  <IconSearch size={14} stroke={1.5} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-tertiary)' }} />
                  <input value={modelSearch} onChange={e => setModelSearch(e.target.value)} placeholder={`Search ${selectedMake.name} models...`} style={{ paddingLeft: 36 }} />
                </div>
                {modelsLoading ? (
                  <div style={{ padding: 20, fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading models...</div>
                ) : (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 16, maxHeight: 380, overflow: 'auto' }}>
                      {filteredModels.map(m => (
                        <div key={m.id} onClick={() => { setSelectedModel(m); setStep(3); }}
                          style={{
                            padding: 12, borderRadius: 'var(--radius-md)', cursor: 'pointer',
                            border: '1px solid var(--color-border-light)', background: 'var(--color-bg-primary)',
                            transition: 'all 0.15s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--color-brand)'}
                          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--color-border-light)'}>
                          <div style={{ aspectRatio: '1.4', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-sm)', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                            {m.heroImageUrl ? <img src={m.heroImageUrl} alt={m.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <IconTractor size={24} stroke={1} color="var(--color-text-tertiary)" />}
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 500 }}>{m.name}</div>
                          <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>{m.yearRange || ''}</div>
                        </div>
                      ))}
                    </div>
                    {filteredModels.length === 0 && (
                      <div style={{ padding: 20, fontSize: 13, color: 'var(--color-text-tertiary)', textAlign: 'center' }}>
                        No matching models in library for {selectedMake.name}.
                      </div>
                    )}
                    <Button variant="secondary" icon={<IconPlus size={14} stroke={1.5} />} onClick={() => setAddingNewModel(true)} fullWidth>
                      Can't find it? Add a new model
                    </Button>
                  </>
                )}
              </>
            )}
          </Card>
        )}

        {/* STEP 3 — Details */}
        {step === 3 && (
          <Card>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div><label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Serial number</label><input value={serialNumber} onChange={e => setSerialNumber(e.target.value)} /></div>
              <div><label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Registration</label><input value={registration} onChange={e => setRegistration(e.target.value)} /></div>
              <div><label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Year</label><input type="number" value={year} onChange={e => setYear(e.target.value)} placeholder="e.g. 2022" /></div>
              <div><label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Current hours</label><input type="number" step="0.1" value={currentHours} onChange={e => setCurrentHours(e.target.value)} /></div>
              <div><label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Purchase date</label><input type="date" value={purchaseDate} onChange={e => setPurchaseDate(e.target.value)} /></div>
              <div><label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>Purchase price (£)</label><input type="number" step="0.01" value={purchasePrice} onChange={e => setPurchasePrice(e.target.value)} /></div>
            </div>
          </Card>
        )}

        {/* STEP 4 — Site */}
        {step === 4 && (
          <Card>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 16 }}>Which site is this machine based at?</div>
            <select value={selectedSite} onChange={e => setSelectedSite(e.target.value)}>
              <option value="">Pick a site...</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Card>
        )}

        {/* STEP 5 — Operator */}
        {step === 5 && (
          <Card>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 16 }}>Assign to an operator, or mark as a pool machine.</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={isPoolMachine} onChange={e => setIsPoolMachine(e.target.checked)} />
              <span>Pool machine (no dedicated operator)</span>
            </label>
            {!isPoolMachine && (
              <select value={selectedOperator?.id || ''} onChange={e => setSelectedOperator(staff.find(s => s.id === e.target.value) || null)}>
                <option value="">Pick an operator...</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.name || s.email}</option>)}
              </select>
            )}
          </Card>
        )}

        {/* STEP 6 — Confirm */}
        {step === 6 && (
          <Card>
            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>Review</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.8 }}>
              <div><strong>{selectedMake?.name}</strong> {selectedMake?._pending && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 'var(--radius-full)', background: 'var(--color-warning-light)', color: 'var(--color-warning)', textTransform: 'uppercase', letterSpacing: '0.04em', marginLeft: 6 }}>Pending</span>} <strong>{selectedModel?.name}</strong> {selectedModel?._pending && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 'var(--radius-full)', background: 'var(--color-warning-light)', color: 'var(--color-warning)', textTransform: 'uppercase', letterSpacing: '0.04em', marginLeft: 6 }}>Pending</span>}</div>
              <div>Category: {selectedCategory}</div>
              {serialNumber && <div>Serial: {serialNumber}</div>}
              {registration && <div>Registration: {registration}</div>}
              {year && <div>Year: {year}</div>}
              {currentHours && <div>Current hours: {currentHours}</div>}
              <div>Site: {sites.find(s => s.id === selectedSite)?.name || '—'}</div>
              <div>Operator: {isPoolMachine ? 'Pool machine' : (selectedOperator?.name || selectedOperator?.email || '—')}</div>
            </div>
          </Card>
        )}

        {/* Footer nav */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
          <Button variant="secondary" onClick={step === 0 ? () => navigate('/machines') : back} icon={<IconArrowLeft size={14} stroke={1.5} />}>
            {step === 0 ? 'Cancel' : 'Back'}
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={next} disabled={!canAdvance()} icon={<IconArrowRight size={14} stroke={1.5} />}>Continue</Button>
          ) : (
            <Button loading={saving} onClick={handleSave} icon={<IconCheck size={14} stroke={1.5} />}>Add machine</Button>
          )}
        </div>
      </div>
    </Layout>
  );
}
